import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TaskCategory,
  TaskWhereInput,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { SelectOption } from 'src/components/general/inputs/select/select';
import {
  TasksPageQueryParams,
  TaskStatusResolver,
} from 'src/components/pages/tasks/shared';
import { useDateHelpers } from 'src/hooks/use-date-helpers';

export const useTaskWhereInputResolver = (
  viewCode?: TasksPageQueryParams['view-code'],
) => {
  const { parseISO } = useDateHelpers();

  return (params: TasksPageQueryParams) => {
    const whereClauses: TaskWhereInput[] = [];

    if (params.q?.advanced) {
      const { advanced } = params.q;

      if (params['view-code'] === 'upcoming') {
        if (!advanced.status || !advanced.due_at_from) {
          return undefined;
        }

        whereClauses.push({
          dueAt: {
            _gt: parseISO(advanced.due_at_from),
          },
          status: {
            _in: advanced.status.map((status) => TaskStatusResolver[status]),
          },
        });

        return { _and: whereClauses };
      }

      if (params['view-code'] === 'overdue') {
        if (!advanced.status || !advanced.due_at_to) {
          return undefined;
        }

        whereClauses.push({
          dueAt: {
            _lte: parseISO(advanced.due_at_to),
          },
          status: {
            _in: advanced.status.map((status) => TaskStatusResolver[status]),
          },
        });

        return { _and: whereClauses };
      }

      if (advanced.due_at_from) {
        whereClauses.push({
          dueAt: {
            _gte: parseISO(advanced.due_at_from),
          },
        });
      }

      if (advanced.due_at_to) {
        whereClauses.push({
          dueAt: {
            _lte: parseISO(advanced.due_at_to),
          },
        });
      }

      if (advanced.title) {
        whereClauses.push({
          title: {
            _contains: advanced.title,
          },
        });
      }

      if (advanced.status && advanced.status.length > 0) {
        whereClauses.push({
          status: {
            _in: advanced.status.map((status) => TaskStatusResolver[status]),
          },
        });
      }

      if (advanced.category) {
        whereClauses.push({
          category: {
            _eq: advanced.category as TaskCategory,
          },
        });
      }

      if (advanced.agent) {
        const agentId = parseInt(advanced.agent, 10);

        if (!Number.isNaN(agentId)) {
          whereClauses.push({
            assignedTo: {
              id: {
                _eq: agentId,
              },
            },
          });
        }
      }

      if (advanced.group) {
        const groupId = parseInt(advanced.group, 10);

        if (!Number.isNaN(groupId)) {
          whereClauses.push({
            assignedTo: {
              groupRelation: {
                id: {
                  _eq: groupId,
                },
              },
            },
          });
        }
      }

      if (advanced.created_by) {
        const creatorId = parseInt(advanced.created_by, 10);

        if (!Number.isNaN(creatorId)) {
          whereClauses.push({
            createdBy: {
              id: {
                _eq: creatorId,
              },
            },
          });
        }
      }
    }

    return whereClauses.length ? { _and: whereClauses } : undefined;
  };
};

export const useTaskStatusOptions = () => {
  const { t } = useTranslation(['tasks']);

  const taskStatusOptions: SelectOption<string>[] = useMemo(
    () => [
      {
        label: t('tasks:filters.status.options.yetToStart'),
        value: 'YET-TO-START',
        icon: 'taskYetToStart',
      },
      {
        label: t('tasks:filters.status.options.inProgress'),
        value: 'IN-PROGRESS',
        icon: 'taskInProgress',
      },
      {
        label: t('tasks:filters.status.options.completed'),
        value: 'COMPLETED',
        icon: 'taskCompleted',
      },
    ],
    [t],
  );

  return taskStatusOptions;
};

export const useTaskCategoryOptions = () => {
  const { t } = useTranslation(['tasks']);

  const taskCategoryOptions: SelectOption<string>[] = useMemo(
    () => [
      {
        label: t('tasks:filters.category.options.call'),
        value: 'CALL',
      },
      {
        label: t('tasks:filters.category.options.email'),
        value: 'EMAIL',
      },
      {
        label: t('tasks:filters.category.options.toDo'),
        value: 'TO_DO',
      },
    ],
    [t],
  );

  return taskCategoryOptions;
};

export const useTaskReminderAtOptions = () => {
  const { t } = useTranslation(['tasks']);

  const taskReminderAtOptions: SelectOption<number>[] = useMemo(
    () => [
      {
        label: t('tasks:emailReminder.atTheTimeOfTheEvent'),
        value: 0,
      },
      {
        label: t('tasks:emailReminder.5minsBefore'),
        value: 5,
      },
      {
        label: t('tasks:emailReminder.15minsBefore'),
        value: 15,
      },
      {
        label: t('tasks:emailReminder.30minsBefore'),
        value: 30,
      },
      {
        label: t('tasks:emailReminder.1hourBefore'),
        value: 60,
      },
      {
        label: t('tasks:emailReminder.2hoursBefore'),
        value: 120,
      },
      {
        label: t('tasks:emailReminder.1dayBefore'),
        value: 1440,
      },
      {
        label: t('tasks:emailReminder.2daysBefore'),
        value: 2880,
      },
      {
        label: t('tasks:emailReminder.1weekBefore'),
        value: 10080,
      },
    ],
    [t],
  );

  return taskReminderAtOptions;
};
